// MemberDataBase.jsx
import MenuBar from '../../../layout/MenuBar';
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import io from 'socket.io-client';
import { useSelector } from 'react-redux';
import {
  Table,
  Button,
  message,
  Spin,
  Form,
  Input,
  Select,
  Card,
  Segmented,
  Tooltip,
  Empty,
  Popconfirm,
} from 'antd';
import AppModal from '../../../layout/popup';
import {
  PlusOutlined,
  CarFilled,
  HomeFilled,
  DashboardFilled,
  ExportOutlined,
  ReloadOutlined,
  EditFilled,
  DeleteFilled,
} from '@ant-design/icons';
import CreateMemberData from './actions/CreateMemberData';
import ViewMemberVehiclesModal from './actions/viewMemberVehicles';
import EditMemberModal from './actions/editMember';
import {
  exportMemberData,
  listMemberData_API,
  removeMemberData_API,
  updateMemberData_API,
} from '../../../../functions/membertData';
import styles from './MemberDataBase.module.css';

const MemberDataBase = () => {
  // ------------------- State หลัก -------------------
  const [dataMembers, setDataMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // pagination state for continuous numbering
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 50 });

  // Modal state
  const [activeModal, setActiveModal] = useState(null); // 'create' | 'view' | 'edit' | null
  const [viewingRecord, setViewingRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [lastUpdatedId, setLastUpdatedId] = useState(null); // เพิ่มตัวแปรเก็บ ID ล่าสุดที่อัปเดต
  const [form] = Form.useForm();
  const user = useSelector((state) => state.user);
  const idTokenResult = localStorage.getItem('token');

  // Header title: prefer village -> name -> email. Keep any 'pash' marker if present
  const _rawName = (user?.village || user?.name || user?.email || '').trim();
  const _cleanName = _rawName.replace(/ต่อpash/gi, '').trim() || 'TRACXPERT';
  const _hasPash = /pash/i.test(_rawName);
  const _phase = (user && (user.phase || user.phase === 0)) ? String(user.phase) : null;
  // build: base name, optional 'pash', optional 'Phase N'
  const headerTitle = `${_cleanName}${_hasPash ? ' pash' : ''}${_phase ? ` Phase ${_phase}` : ''}`;

  // เรียง
  const [sortMode, setSortMode] = useState('Date'); // Date | Low | High

  // ค้นหา/กรอง
  const [filterMode, setFilterMode] = useState('all'); // all | licensePlate | province | houseNumber
  const [searchRaw, setSearchRaw] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // debounce แล้ว
  const [provinceFilter, setProvinceFilter] = useState(undefined);

  // Debounce input
  const debounceRef = useRef(null);
  const handleInputChange = (e) => {
    const v = e?.target?.value ?? '';
    setSearchRaw(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(v.trim());
    }, 300);
  };
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const searchDeferred = searchQuery;

  // ------------------- Socket (ปิดให้สะอาด) -------------------
  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_SOCKETIO, { transports: ['websocket'] });
    return () => {
      try {
        newSocket.close();
      } catch (_) { /* no-op */ }
    };
  }, []);

  // ------------------- ดึงข้อมูล -------------------
  const fetchData = useCallback(async () => {
    try {
      const vId = user.id || localStorage.getItem('villageId');
      const res = await listMemberData_API(idTokenResult, vId);

      if (!res?.success) throw new Error('API return unsuccessful');

      let fetchedData = (res.data || []).map((member) => ({
        ...member,
        vehicles: Array.isArray(member.vehicles) ? member.vehicles : [],
      }));

      // เรียงลำดับข้อมูลตาม sortMode ที่เลือก
      switch (sortMode) {
        case 'Low': {
          fetchedData.sort((a, b) => {
            const na = parseInt(a.houseNumber?.match(/\d+/g)?.join('') || '0', 10);
            const nb = parseInt(b.houseNumber?.match(/\d+/g)?.join('') || '0', 10);
            return na - nb;
          });
          break;
        }
        case 'High': {
          fetchedData.sort((a, b) => {
            const na = parseInt(a.houseNumber?.match(/\d+/g)?.join('') || '0', 10);
            const nb = parseInt(b.houseNumber?.match(/\d+/g)?.join('') || '0', 10);
            return nb - na;
          });
          break;
        }
        case 'Date':
        default: {
          fetchedData.sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt || 0) -
              new Date(a.updatedAt || a.createdAt || 0)
          );
          break;
        }
      }

      setDataMembers(fetchedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('ไม่สามารถดึงข้อมูลสมาชิกได้');
    } finally {
      setLoading(false);
    }
  }, [idTokenResult, user?.id, sortMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------- Modal helpers -------------------
  const openModal = useCallback((type, record = null) => {
    if (type === 'view') {
      setViewingRecord(record);
      setActiveModal('view');
      return;
    }
    if (type === 'edit') {
      setEditingRecord(record);
      setActiveModal('edit');
      return;
    }
    setActiveModal('create');
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setViewingRecord(null);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  // ------------------- Actions -------------------

  const handleExportMemberData = useCallback(async () => {
    setExportLoading(true);
    try {
      const res = await exportMemberData(idTokenResult, user);
      if (res?.success) {
        message.success('เริ่มการส่งออกแล้ว (ตรวจสอบกล่องดาวน์โหลด/อีเมล หากระบบส่งไฟล์ผ่านอีเมล)');
      } else {
        throw new Error(res?.message || 'export failed');
      }
    } catch (error) {
      console.error('Error exporting member data:', error);
      message.error(`ไม่สามารถส่งออกข้อมูลได้: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  }, [idTokenResult, user]);

  const handleCreateSuccess = useCallback((newData) => {
    if (newData) {
      setDataMembers((prev) => {
        const next = [newData, ...prev];
        if (sortMode === 'Date') {
          next.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        }
        return [...next];
      });
      setTablePagination((p) => ({ ...p, current: 1 }));
    }
    closeModal();
  }, [closeModal, sortMode]);

  const handleViewDetails = useCallback(
    (record) => {
      openModal('view', record);
    },
    [openModal]
  );

  const handleEdit = useCallback(
    (record) => {
      openModal('edit', record);
    },
    [openModal]
  );

  const handleEditConfirm = useCallback(async () => {
    try {
      const values = await form.validateFields();
      // map vehicleType -> type ตามหลังบ้าน
      const payload = {
        ...editingRecord,
        ...values,
        vehicles: (values.vehicles || []).map((v) => ({
          id: v.id, // ส่ง id ไปด้วยเพื่อให้อัปเดตตัวเดิม
          type: v.vehicleType,
          licensePlate: v.licensePlate,
          province: v.province,
        })),
      };

      setEditLoading(true);
      const res = await updateMemberData_API(idTokenResult, payload);

      if (res?.success && res?.data) {
        setDataMembers((prev) => {
          const updated = prev.map((m) => {
            const mId = m.id || m._id;
            const resId = res.data.id || res.data._id;
            return String(mId) === String(resId) ? res.data : m;
          });
          return [...updated];
        });

        // ล็อคตำแหน่งเดิมไว้ เพื่อให้ผู้ใช้เห็นค่าที่เปลี่ยนในแถวเดิมทันที
        setLastUpdatedId(res.data.id || res.data._id);
      }

      message.success('แก้ไขข้อมูลสำเร็จ');
      closeModal();
    } catch (error) {
      console.error('Error editing member:', error);
      const errorMsg = error.response?.data?.error || error.message || '';
      if (errorMsg.includes('Unique constraint failed')) {
        message.error('ไม่สามารถบันทึกได้: บ้านเลขที่นี้มีอยู่ในระบบแล้ว');
      } else {
        message.error('ไม่สามารถแก้ไขข้อมูลได้');
      }
    } finally {
      setEditLoading(false);
    }
  }, [editingRecord, form, idTokenResult, closeModal]);

  const handleDelete = useCallback(
    async (record) => {
      try {
        await removeMemberData_API(idTokenResult, record);
        message.success('ลบข้อมูลสำเร็จ');

        // อัปเดต State ทันทีโดยการกรองตัวที่ลบออก
        setDataMembers((prev) =>
          prev.filter((m) => {
            const mId = m.id || m._id;
            const targetId = record.id || record._id;
            return String(mId) !== String(targetId);
          })
        );

        // ถ้ากำลังเปิด view/edit ของ record เดิม ให้ปิด
        const rid = record.id || record._id;
        if (
          (activeModal === 'view' && viewingRecord && (viewingRecord.id || viewingRecord._id) === rid) ||
          (activeModal === 'edit' && editingRecord && (editingRecord.id || editingRecord._id) === rid)
        ) {
          closeModal();
        }
      } catch (error) {
        console.error('Error deleting member:', error);
        message.error('ไม่สามารถลบข้อมูลได้');
      }
    },
    [idTokenResult, activeModal, viewingRecord, editingRecord, closeModal]
  );

  // ------------------- Province Options -------------------
  const provinceOptions = useMemo(() => {
    const setP = new Set();
    (dataMembers || []).forEach((m) =>
      (m.vehicles || []).forEach((v) => {
        if (v?.province) setP.add(String(v.province).trim());
      })
    );
    return Array.from(setP).sort((a, b) => a.localeCompare(b, 'th'));
  }, [dataMembers]);

  // ------------------- Highlight Helper -------------------
  const highlight = (text, q) => {
    if (!q || !text) return text;
    const idx = String(text).toLowerCase().indexOf(String(q).toLowerCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <>
        {before}
        <mark className={styles.hl}>{match}</mark>
        {after}
      </>
    );
  };

  // ------------------- กรองข้อมูล -------------------
  const displayedData = useMemo(() => {
    const q = searchDeferred;
    const arrBase = dataMembers;

    let arr = arrBase;

    if (filterMode === 'licensePlate' && q) {
      arr = arrBase.filter((m) =>
        (m.vehicles || []).some((v) =>
          (v.licensePlate || '').toLowerCase().includes(q.toLowerCase())
        )
      );
    }

    if (filterMode === 'houseNumber' && q) {
      arr = arrBase.filter((m) =>
        (m.houseNumber || '').toLowerCase().includes(q.toLowerCase())
      );
    }

    if (filterMode === 'province') {
      arr = arrBase;
      const normalize = (s) => String(s || '').trim().toLowerCase();
      if (provinceFilter) {
        arr = arr.filter((m) =>
          (m.vehicles || []).some(
            (v) => normalize(v.province) === normalize(provinceFilter)
          )
        );
      }
      if (q) {
        arr = arr.filter((m) =>
          (m.vehicles || []).some((v) =>
            (v.licensePlate || '').toLowerCase().includes(q.toLowerCase())
          )
        );
      }
    }

    if (filterMode === 'all' && q) {
      arr = arrBase.filter((m) => {
        const hn = (m.houseNumber || '').toLowerCase().includes(q.toLowerCase());
        const lp = (m.vehicles || []).some((v) =>
          (v.licensePlate || '').toLowerCase().includes(q.toLowerCase())
        );
        const pv = (m.vehicles || []).some((v) =>
          (v.province || '').toLowerCase().includes(q.toLowerCase())
        );
        return hn || lp || pv;
      });
    }

    return arr;
  }, [dataMembers, filterMode, searchDeferred, provinceFilter]);

  // ------------------- สรุปยอด -------------------
  const { carCount, motorcycleCount, totalHouseNumbers, totalVehicles } =
    useMemo(() => {
      let car = 0;
      let motor = 0;
      const houseSet = new Set();
      dataMembers.forEach((m) => {
        if (m.houseNumber) houseSet.add(m.houseNumber);
        (m.vehicles || []).forEach((v) => {
          if (v.type === 'รถยนต์') car += 1;
          if (v.type === 'รถมอเตอร์ไซค์') motor += 1;
        });
      });
      return {
        carCount: car,
        motorcycleCount: motor,
        totalHouseNumbers: houseSet.size,
        totalVehicles: car + motor,
      };
    }, [dataMembers]);

  // eslint-disable-next-line no-unused-vars
  const sortLabel = useMemo(() => {
    if (sortMode === 'Low') return 'บ้านเลขที่น้อยไปมาก';
    if (sortMode === 'High') return 'บ้านเลขที่มากไปน้อย';
    return 'อัปเดตล่าสุด';
  }, [sortMode]);

  // ------------------- Reset Filters -------------------
  const resetFilters = useCallback(() => {
    setFilterMode('all');
    setSearchRaw('');
    setSearchQuery('');
    setProvinceFilter(undefined);
  }, []);

  // ------------------- Columns -------------------
  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        align: 'center',
        render: (id) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>#{id}</span>,
      },
      {
        title: 'ลำดับ',
        key: 'rowIndex',
        align: 'center',
        width: 88,
        render: (_t, _r, index) => {
          const current = tablePagination?.current || 1;
          const pageSize = tablePagination?.pageSize || 50;
          return (current - 1) * pageSize + index + 1;
        },
      },
      {
        title: 'บ้านเลขที่',
        dataIndex: 'houseNumber',
        key: 'houseNumber',
        align: 'center',
        render: (text) => {
          const content =
            filterMode === 'houseNumber' || filterMode === 'all'
              ? highlight(text, searchDeferred)
              : text;
          return <span style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>{content}</span>;
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status) => (
          <span className={`${styles.statusBadge} ${status === 'เจ้าของ' ? styles.statusOwner : styles.statusTenant}`}>
            {status}
          </span>
        )
      },
      {
        title: 'จำนวนรถ',
        key: 'numberVehicles',
        align: 'center',
        render: (_t, record) => <span>{record?.vehicles?.length || 0} คัน</span>,
      },
      {
        title: 'ทะเบียน',
        key: 'licensePlateSample',
        align: 'center',
        render: (_t, record) => {
          const vehicles = record.vehicles || [];
          const q = (searchDeferred || '').toLowerCase();
          let match = null;
          if (q) {
            match = vehicles.find((v) =>
              (v.licensePlate || '').toLowerCase().includes(q)
            );
          }
          const fallback = vehicles[0];
          const plate =
            (match && match.licensePlate) ||
            (fallback && fallback.licensePlate) ||
            '-';
          return <span>{highlight(plate, q)}</span>;
        },
      },
      {
        title: 'แก้ไขล่าสุด',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        className: styles.updatedColumn,
        onHeaderCell: () => ({ className: styles.updatedHeader }),
        align: 'center',
        render: (_text, record) => {
          const updatedDate = record.updatedAt || record.createdAt;
          const createdDate = record.createdAt;

          return (
            <Tooltip
              title={
                <div style={{ textAlign: 'center' }}>
                  {createdDate && (
                    <div>
                      สร้างเมื่อ:{' '}
                      {new Date(createdDate).toLocaleString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </div>
                  )}
                  {updatedDate && record.updatedAt && (
                    <div style={{ marginTop: 4 }}>
                      แก้ไขเมื่อ:{' '}
                      {new Date(updatedDate).toLocaleString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </div>
                  )}
                </div>
              }
            >
              <div className={styles.updatedCell}>
                <div className={styles.updatedDate}>
                  {updatedDate
                    ? new Date(updatedDate).toLocaleDateString('th-TH', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                    : '-'}
                </div>
                <div className={styles.updatedTime}>
                  {updatedDate
                    ? new Date(updatedDate).toLocaleTimeString('th-TH', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    : ''}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: 'จัดการ',
        key: 'actions',
        align: 'right',
        width: 220,
        onCell: () => ({
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
          onDoubleClick: (e) => e.stopPropagation(),
        }),
        render: (_t, record) => (
          <div
            className={styles.actionBtns}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="แก้ไข">
              <Button
                icon={<EditFilled />}
                className={styles.editBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(record);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
              />
            </Tooltip>

            <Popconfirm
              title="ยืนยันการลบข้อมูล"
              description={
                <div style={{ maxWidth: 250 }}>
                  คุณต้องการลบข้อมูลสมาชิก<br />
                  <strong>บ้านเลขที่: {record.houseNumber}</strong>
                  <br />
                  ใช่หรือไม่?
                </div>
              }
              onConfirm={() => handleDelete(record)}
              okText="ยืนยัน"
              cancelText="ยกเลิก"
              okType="danger"
            >
              <Tooltip title="ลบข้อมูล">
                <Button
                  icon={<DeleteFilled />}
                  danger
                  className={styles.deleteBtn}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [filterMode, searchDeferred, handleEdit, handleDelete, tablePagination]
  );

  // ------------------- Render -------------------
  return (
    <div className={`${styles.page} page-background page-dashboard`}>
      {/* MenuBar ตรึงด้านบน */}
      <div className={styles.menuBarFixed}>
        <MenuBar />
      </div>

      {/* เนื้อหา */}
      <div className={styles.contentArea}>
        <div className={styles.pageHeader}>
          <div className={styles.pageIntro}>
            <div className={styles.introCard}>
              <div className={styles.headerCopy}>
                <span className={styles.eyebrow}>TRACXPERT MANAGEMENT</span>
                <h1 className={styles.title}>{headerTitle}</h1>
              </div>
              <p className={styles.subtitle}>
                ฐานข้อมูลทะเบียนรถหมู่บ้านสำหรับค้นหา จัดการ และตรวจสอบข้อมูลได้อย่างเป็นระเบียบบนทุกขนาดหน้าจอ
              </p>

              <div className={styles.statsSection} style={{ marginTop: '16px' }}>
                <Card variant="borderless" className={styles.statsCard}>
                  <CarFilled className={`${styles.statsIcon} ${styles.iconCar}`} />
                  <div className={styles.statsValue}>{carCount}</div>
                  <div className={styles.statsLabel}>รถยนต์</div>
                </Card>

                <Card variant="borderless" className={styles.statsCard}>
                  <CarFilled className={`${styles.statsIcon} ${styles.iconMotor}`} />
                  <div className={styles.statsValue}>{motorcycleCount}</div>
                  <div className={styles.statsLabel}>มอเตอร์ไซค์</div>
                </Card>

                <Card variant="borderless" className={styles.statsCard}>
                  <HomeFilled className={`${styles.statsIcon} ${styles.iconHome}`} />
                  <div className={styles.statsValue}>{totalHouseNumbers}</div>
                  <div className={styles.statsLabel}>ที่อยู่อาศัย</div>
                </Card>

                <Card variant="borderless" className={styles.statsCard}>
                  <DashboardFilled className={`${styles.statsIcon} ${styles.iconDash}`} />
                  <div className={styles.statsValue}>{totalVehicles}</div>
                  <div className={styles.statsLabel}>รถทั้งหมด</div>
                </Card>
              </div>
            </div>
          </div>
        </div>

        <section className={styles.tableContainer}>
          <div className={styles.tablePanel}>
            <div className={styles.sectionHeading}>
              <div>
                <h2 className={styles.sectionTitle}>ค้นหาและจัดการข้อมูล</h2>
                <p className={styles.sectionSubtitle}>เลือกมุมมองการค้นหา เพิ่มข้อมูลใหม่ หรือส่งออกข้อมูลจากแถบควบคุมนี้</p>
              </div>
            </div>

            <div className={styles.filterBar}>
              <div className={styles.controlBlock}>
                <span className={styles.controlLabel}>โหมดค้นหา</span>
                <div className={styles.segmentWrap}>
                  <Segmented
                    value={filterMode}
                    onChange={(v) => {
                      if (v !== filterMode) {
                        setFilterMode(v);
                        setSearchRaw('');
                        setSearchQuery('');
                      }
                      if (v !== 'province') setProvinceFilter(undefined);
                    }}
                    options={[
                      { label: 'ทั้งหมด', value: 'all' },
                      { label: 'ทะเบียน', value: 'licensePlate' },
                      { label: 'จังหวัด', value: 'province' },
                      { label: 'บ้านเลขที่', value: 'houseNumber' },
                    ]}
                    className={styles.segmented}
                  />
                </div>
              </div>

              <div className={styles.controlBlock}>
                <span className={styles.controlLabel}>
                  {filterMode === 'province' ? 'ค้นหาและกรองจังหวัด' : 'ค้นหาข้อมูล'}
                </span>
                <div
                  className={`${styles.searchWrap} ${filterMode === 'province' ? styles.searchWrapWide : ''
                    }`}
                >
                  {filterMode === 'province' ? (
                    <>
                      <Select
                        allowClear
                        placeholder="เลือกจังหวัด"
                        value={provinceFilter}
                        onChange={setProvinceFilter}
                        className={styles.provinceSelect}
                        options={provinceOptions.map((p) => ({ value: p, label: p }))}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                      />
                      <Input
                        placeholder="ค้นหาเลขทะเบียนภายในจังหวัด"
                        allowClear
                        value={searchRaw}
                        onChange={handleInputChange}
                        className={styles.searchInput}
                        style={{ width: '100%' }}
                      />
                    </>
                  ) : (
                    <Input
                      placeholder={
                        filterMode === 'licensePlate'
                          ? 'ค้นหาตามเลขทะเบียน'
                          : filterMode === 'houseNumber'
                            ? 'ค้นหาตามบ้านเลขที่'
                            : 'ค้นหาทั้งหมด (ทะเบียน/จังหวัด/บ้านเลขที่)'
                      }
                      allowClear
                      value={searchRaw}
                      onChange={handleInputChange}
                      className={styles.searchInput}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
              </div>

              <div className={`${styles.controlBlock} ${styles.actionsBlock}`}>
                <span className={styles.controlLabel}>คำสั่ง</span>
                <div className={styles.actionsRight}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={resetFilters}
                    className={styles.resetBtn}
                  >
                    รีเซ็ต
                  </Button>

                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openModal('create')}
                    className={styles.primaryButton}
                  >
                    เพิ่มข้อมูล
                  </Button>

                  <Popconfirm
                    title="ยืนยันการส่งออกข้อมูล"
                    description="คุณต้องการส่งออกข้อมูลเป็นไฟล์ Excel ใช่หรือไม่?"
                    onConfirm={handleExportMemberData}
                    okText="ยืนยัน"
                    cancelText="ยกเลิก"
                  >
                    <Button
                      icon={<ExportOutlined />}
                      loading={exportLoading}
                      className={styles.secondaryButton}
                    >
                      ส่งออก
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </div>

            <div className={styles.tablePanelHeader}>
              <div className={styles.tableHeaderLeft}>
                <h2 className={styles.tableTitle}>รายการข้อมูลทะเบียนรถ</h2>
                <p className={styles.tableSubtitle}>คลิกที่แถวเพื่อดูรายละเอียดรถของบ้านเลขที่นั้น</p>
              </div>

              <div className={styles.tableHeaderRight}>
                <div className={styles.resultMeta}>
                  พบข้อมูล <b>{displayedData.length}</b> แถว
                </div>

                <div className={styles.sortControl}>
                  <span className={styles.controlLabel}>จัดเรียง</span>
                  <Segmented
                    value={sortMode}
                    onChange={(value) => setSortMode(value)}
                    options={[
                      { label: 'น้อย→มาก', value: 'Low' },
                      { label: 'มาก→น้อย', value: 'High' },
                      { label: 'ล่าสุด', value: 'Date' },
                    ]}
                    className={styles.segmented}
                  />
                </div>
              </div>
            </div>

            <div className={styles.tableInner}>
              {loading ? (
                <div className={styles.loading}>
                  <Spin size="large" />
                </div>
              ) : displayedData.length === 0 ? (
                <div className={styles.empty}>
                  <Empty description="ไม่พบข้อมูลตามเงื่อนไขที่ค้นหา/กรอง" />
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={displayedData.map((member, idx) => ({
                    key: member.id || member._id || idx,
                    ...member,
                    rowIndex: idx,
                  }))}
                  rowClassName={(record) => {
                    const rid = record.id || record._id;
                    return String(rid) === String(lastUpdatedId) ? styles.rowHighlightRecent : '';
                  }}
                  rowKey={(r) => r.id || r._id || r.key}
                  pagination={{
                    current: tablePagination.current,
                    pageSize: tablePagination.pageSize,
                    showSizeChanger: false,
                  }}
                  onChange={(pag) => {
                    setTablePagination({ current: pag.current || 1, pageSize: pag.pageSize || 50 });
                  }}
                  sticky={{ offsetHeader: 64 }}
                  size="middle"
                  className={styles.dataTable}
                  scroll={{ x: 'max-content' }}
                  onRow={(record) => ({
                    onClick: (e) => {
                      const el = e.target.closest('button, a, input, .ant-popover, .ant-popconfirm');
                      if (el) return;
                      handleViewDetails(record);
                    },
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </div>
          </div>
        </section>

        {/* Modals */}
        <AppModal
          icon={<PlusOutlined />}
          title="สร้างฐานข้อมูลลูกค้าใหม่"
          subtitle="กรอกข้อมูลสมาชิกและรถเพื่อเพิ่มเข้าระบบ"
          open={activeModal === 'create'}
          onCancel={closeModal}
          footer={null}
          width={800}
          key="create"
        >
          <CreateMemberData onSuccess={handleCreateSuccess} onCancel={closeModal} />
        </AppModal>

        <ViewMemberVehiclesModal
          open={activeModal === 'view'}
          onCancel={closeModal}
          viewingRecord={viewingRecord}
        />

        <EditMemberModal
          open={activeModal === 'edit'}
          onCancel={closeModal}
          onOk={handleEditConfirm}
          confirmLoading={editLoading}
          form={form}
          record={editingRecord}
        />
      </div>


    </div>
  );
};

export default MemberDataBase;
